import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { EditorLink } from '@utils/tiptapEditorLink';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { uploadAttachment } from '../../../../../server/api';
import { uploadOssFile } from '../../../../../server/oss-upload';
import Toast from '@components/Toast';
import styles from './TiptapEditor.module.scss';
import './TiptapEditor.scss'; // 引入全局样式
import classNames from 'classnames';
import { MAX_THREAD_IMAGES, countNormalImagesFromHtml } from '../../../../../utils/setPostLimits';
import { isSupportedImageFile } from '../../../../../utils/imageFile';
import { prepareEditorHtmlForHydration } from '../../../../../utils/tiptapEditorContent';

interface TiptapEditorH5Props {
    content?: string;
    onContentChange?: (content: string) => void;
    imageInputRef?: React.RefObject<HTMLInputElement | null>;
    editorRef?: React.MutableRefObject<any>;
    videoInputRef?: React.RefObject<HTMLInputElement | null>;
    onVideoUpload?: (data: { url: string; id: number; file?: File; videoCoverUrl?: string }) => void;
    hasVideo?: boolean;
}

const TiptapEditorH5: React.FC<TiptapEditorH5Props> = ({
    content = '',
    onContentChange,
    imageInputRef: externalImageInputRef,
    editorRef,
    videoInputRef: externalVideoInputRef,
    onVideoUpload,
}) => {
    const internalImageInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = externalImageInputRef || internalImageInputRef;
    const internalVideoInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = externalVideoInputRef || internalVideoInputRef;
    const [uploadingImages, setUploadingImages] = useState(false);

    // 扩展 Image，支持 data-emoji / width / height 属性渲染
    const CustomImage = Image.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                width: {
                    default: null,
                    parseHTML: element => element.getAttribute('width'),
                    renderHTML: attributes => (attributes.width ? { width: attributes.width } : {}),
                },
                height: {
                    default: null,
                    parseHTML: element => element.getAttribute('height'),
                    renderHTML: attributes => (attributes.height ? { height: attributes.height } : {}),
                },
                style: {
                    default: null,
                    parseHTML: element => element.getAttribute('style'),
                    renderHTML: attributes => (attributes.style ? { style: attributes.style } : {}),
                },
                'data-emoji': {
                    default: null,
                    parseHTML: element => element.getAttribute('data-emoji'),
                    renderHTML: attributes => (attributes['data-emoji'] ? { 'data-emoji': attributes['data-emoji'] } : {}),
                },
            } as any;
        },
    });

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false,
                paragraph: {
                    HTMLAttributes: {
                        class: 'editor-paragraph',
                    },
                },
            }),
            EditorLink,
            Color,
            TextStyle,
            Underline,
            CustomImage.configure({
                inline: true,
                allowBase64: true,
                HTMLAttributes: {
                    class: 'editor-image',
                },
            }),
            Placeholder.configure({ 
                placeholder: '添加正文...' 
            }),
        ],
        content: prepareEditorHtmlForHydration(content),
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onContentChange?.(html);
        },
        editorProps: {
            attributes: {
                class: 'tiptap-editor-content tiptap-editor-content--h5',
            },
            handlePaste: (view, event) => {
                // 获取粘贴的数据
                const items = event.clipboardData?.items;
                if (!items) return false;

                // 查找图片类型的数据
                const imageItems: DataTransferItem[] = [];
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                        imageItems.push(items[i]);
                    }
                }

                // 如果有图片，拦截默认行为并上传
                if (imageItems.length > 0) {
                    event.preventDefault();

                    const currentImageCount = countNormalImagesFromHtml(view.dom.innerHTML || content || '');
                    const remainingSlots = MAX_THREAD_IMAGES - currentImageCount;
                    if (remainingSlots <= 0) {
                        toastError(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片`);
                        return true;
                    }

                    const filesToUpload = imageItems
                        .map(item => item.getAsFile())
                        .filter((file): file is File => !!file)
                        .slice(0, remainingSlots);

                    if (filesToUpload.length < imageItems.length) {
                        toastError(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片，超出的图片未添加`);
                    }
                    
                    // 按顺序上传，避免第一张完成时覆盖其余图片的上传提示。
                    setUploadingImages(true);
                    void (async () => {
                        try {
                            for (const file of filesToUpload) {
                                const formData = new FormData();
                                formData.append('type', '1');
                                formData.append('file', file);
                                const closeLoading = Toast.loading({
                                    content: '图片上传中，请稍候...',
                                    duration: 0,
                                });
                                try {
                                    const response = await uploadAttachment(formData).finally(closeLoading);
                                    const imageData = response.data as { url?: string; webpUrl?: string } | undefined;
                                    const imageUrl = imageData?.webpUrl || imageData?.url;
                                    if (response.code === 0 && imageUrl) {
                                        // 上传成功，插入图片
                                        view.dispatch(
                                            view.state.tr.replaceSelectionWith(
                                                view.state.schema.nodes.image.create({ src: imageUrl })
                                            )
                                        );
                                    } else {
                                        toastError(response.message || '图片上传失败');
                                    }
                                } catch (error) {
                                    console.error('[TiptapEditorH5] 粘贴图片上传失败:', error);
                                    toastError('图片上传失败');
                                }
                            }
                        } finally {
                            setUploadingImages(false);
                        }
                    })();

                    return true; // 已处理
                }

                return false; // 未处理，使用默认行为
            },
        },
    });

    // Toast 封装：若不可用则降级为 alert
    const toastError = (msg: string) => {
        try { (Toast as any)?.error ? Toast.error(msg) : alert(msg); } catch { alert(msg); }
    };

    // 将 editor 实例暴露给父组件
    useEffect(() => {
        if (editorRef) {
            editorRef.current = editor;
        }
    }, [editor, editorRef]);

    // 图片上传处理
    const handleImageClick = () => {
        imageInputRef.current?.click();
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const currentImageCount = countNormalImagesFromHtml(editor?.getHTML() || content || '');
        const remainingSlots = MAX_THREAD_IMAGES - currentImageCount;
        if (remainingSlots <= 0) {
            toastError(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片`);
            e.target.value = '';
            return;
        }

        const selectedFiles = Array.from(files);
        const filesToUpload = selectedFiles.slice(0, remainingSlots);
        if (filesToUpload.length < selectedFiles.length) {
            toastError(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片，超出的图片未添加`);
        }

        setUploadingImages(true);

        for (const file of filesToUpload) {
            try {
                // 验证文件类型
                if (!isSupportedImageFile(file)) {
                    toastError('请选择图片文件');
                    continue;
                }

                // 验证文件大小 (限制 50MB)
                if (file.size > 50 * 1024 * 1024) {
                    toastError('图片大小不能超过 50MB');
                    continue;
                }

                // 创建 FormData 并上传
                const formData = new FormData();
                formData.append('type', '1'); // 1 = 图片
                formData.append('file', file);

                const closeLoading = Toast.loading({
                    content: '图片上传中，请稍候...',
                    duration: 0,
                });
                const response = await uploadAttachment(formData).finally(closeLoading);
                const imageData = response.data as { url?: string; webpUrl?: string } | undefined;
                const imageUrl = imageData?.webpUrl || imageData?.url;

                if (response.code === 0 && imageUrl) {
                    // 上传成功，插入真实 URL
                    if (editor) {
                        editor.chain().focus().setImage({ src: imageUrl }).run();
                    }
                    const updatedImageCount = countNormalImagesFromHtml(editor?.getHTML() || content || '');
                    if (updatedImageCount >= MAX_THREAD_IMAGES) {
                        toastError(`已达到图文图片上限 ${MAX_THREAD_IMAGES} 张`);
                    }
                } else {
                    toastError(response.message || '图片上传失败');
                }
            } catch (error: any) {
                console.error('图片上传失败:', error);
                toastError('图片上传失败: ' + (error.message || '未知错误'));
            }
        }

        setUploadingImages(false);
        // 清空输入框，允许重复选择
        e.target.value = '';
    };

    // 视频上传处理 - 使用OSS直传（与PC端保持一致）
    const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('video/')) {
            toastError('请选择视频文件');
            e.target.value = '';
            return;
        }

        // 验证文件大小 (限制 500MB)
        if (file.size > 500 * 1024 * 1024) {
            toastError('视频大小不能超过 500MB');
            e.target.value = '';
            return;
        }

        // 显示加载中
        const closeLoading = Toast.loading({
            content: '视频上传中，请稍候...',
            duration: 0,
        });

        try {
            // 使用OSS直传上传视频（与PC端保持一致）
            const uploadDir = 'public/videos/' + new Date().toISOString().split('T')[0].replace(/-/g, '/');
            const result = await uploadOssFile(file, uploadDir, (percent: number) => {
                console.log('[TiptapEditorH5] 视频上传进度:', percent + '%');
            });

            closeLoading?.();

            if (result.code !== 0) {
                toastError(result.msg || '视频上传失败');
                e.target.value = '';
                return;
            }

            // 上传成功，获取返回的视频信息
            const videoData = result.data;
            console.log('[TiptapEditorH5] 视频上传成功:', videoData);

            // 调用父组件回调，传递视频数据
            onVideoUpload?.({ 
                url: videoData?.url || videoData?.ossVideoUrl, 
                id: videoData?.id, 
                file,
                videoCoverUrl: videoData?.videoCoverUrl || videoData?.cover_url
            });
        } catch (error: any) {
            closeLoading?.();
            console.error('[TiptapEditorH5] 视频上传失败:', error);
            toastError('视频上传失败: ' + (error?.message || '未知错误'));
        }

        // 清空输入框，允许重复选择
        e.target.value = '';
    };

    useEffect(() => {
        const hydratedContent = prepareEditorHtmlForHydration(content);
        if (editor && hydratedContent !== editor.getHTML()) {
            editor.commands.setContent(hydratedContent);
        }
    }, [content, editor]);

    return (
        <>
            <div className={classNames(styles.editorWrapper,'editor-h5') }>
                <EditorContent editor={editor} />
            </div>
            {/* 隐藏的图片上传input */}
            <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageChange}
            />
            {/* 隐藏的视频上传input */}
            <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleVideoChange}
            />
        </>
    );
};

export default TiptapEditorH5;
